/**
 * La planta editada, bajada como PDF vectorial.
 *
 * Pedido de Daniel (16-oct-2026): «en lugar de exportar en modo render, que se
 * pueda entregar un PDF como los que se toman de base, que tengan la misma
 * gráfica, que estén acotados […] y que venga el área total de la casa». El
 * render de venta sigue existiendo y no se toca: son dos entregas distintas —el
 * render es una imagen para vender, esto es un PLANO para construir y medir—.
 *
 * ── Qué pasó con la primera entrega ──
 *
 * La primera versión exportaba «lo que ya se ve»: muros macizos, cotas sobre el
 * eje de cada pared y el cuadro de áreas. Daniel la rechazó el mismo día —«le
 * faltan muchísimas cosas al plano, mirá la referencia y compará: faltan las
 * áreas, falta el mobiliario, faltan las tramas, los fondos; las cotas deben
 * verse como en el plano, alejadas, con líneas de referencia. Esto está en
 * pañales»—. Tenía razón: un plano no es el lienzo del editor impreso.
 *
 * Así que esta versión está medida contra la lámina de obra real
 * (`CroquisBN-421.pdf`, A3 apaisado, 25 362 líneas), no imaginada. Lo que salió
 * de esa medición y está puesto acá:
 *
 * | Rasgo | Lo medido en la lámina | Lo que hace este archivo |
 * |---|---|---|
 * | Trama de muro | 17 472 segmentos grises `0.753`, a 0° y **60°** | rayado a 60° recortado al muro |
 * | Marca de cota | 352 marcas de **2,64 pt**, a 75° y 15° | `MARCA`, girada 75° sobre la línea |
 * | Cadenas de cota | a **6,8–10,1 pt** del borde del plano | `SEP_PARCIAL` / `SEP_TOTAL` |
 * | Línea de referencia | sobresale **4,4 pt** de la línea de cota | `SOBRA` |
 * | Número de cota | **5,2 pt** ArialMT | `COTA = 5.2` |
 * | Fondos | 64 rellenos de piso + 173 blancos | relleno por clase de región |
 *
 * Dos cosas de la referencia que a propósito NO se copian: sus rótulos de
 * recinto son una ANOTACIÓN posterior (SegoeUI 8 pt, no ArialMT — se los
 * escribió alguien encima del PDF) y no llevan el m² al lado. Acá el rótulo
 * sale del modelo y SÍ lleva su área: es lo que Daniel pide leer de un plano.
 *
 * Sigue sin llevar cajetín ni formato de lámina —escalón 2—, detenido a
 * propósito hasta que mande una lámina de obra que medir: un cajetín inventado
 * es peor que ninguno, porque se imprime, se firma y se entrega.
 *
 * ── Por qué se escribe a mano y no con Blender, un CAD o la VM de Vagon ──
 *
 * Porque el editor YA dibuja en vectores: el lienzo es un `<svg>` de verdad, no
 * un lienzo rasterizado. Cualquiera de esas tres opciones obligaría a bajar a
 * imagen (y perder la escala, que es justamente lo que hace que esto sea un
 * plano) o a arrastrar una licencia y una máquina prendida para algo que acá
 * son operadores de pintado.
 *
 * ── La escala es el dato, no un detalle ──
 *
 * Un punto PostScript es 1/72 de pulgada. A escala 1:75, un metro de casa mide
 * `PT_POR_METRO / 75` sobre el papel. Eso es lo que permite tomar la regla del
 * visor y leer medidas reales, y es lo que verifica el banco: reabrir el PDF y
 * comprobar que una pared de 3,00 m mide lo que tiene que medir. Mientras eso
 * no esté medido, esto es «parece bien», no «está bien».
 */
import { createElement } from 'react';
import type { ReactElement, ReactNode } from 'react';

import type { Planta, Pared, Libre, Cota, Mueble, Clase } from './tipos';
import { CLASES, ROTULO_CLASE } from './tipos';
import type { Medicion } from './medirPlanta';
import { caja, tramos, anilloDeCeldas, cuadradosDeEsquina } from './medirPlanta';
import { SimboloMueble } from './muebles';
import { desdeArbol } from './importar-simbolo';
import type { NodoSVG } from './importar-simbolo';

/** Puntos PostScript por metro. Un punto es 1/72 de pulgada. */
const PT_POR_METRO = 72 / 0.0254;

/** Un milímetro de papel, en puntos. Las medidas de hoja se piensan en mm. */
const MM = 72 / 25.4;

/** La escala corriente del catálogo: las láminas del croquis dicen 1:75. */
export const ESCALA_POR_DEFECTO = 75;

export interface OpcionesLamina {
  /** Lo que se escribe arriba: «Planta Arquitectónica 1er Nivel» o el modelo. */
  titulo: string;
  /** Segunda línea, más chica. El nombre del modelo, por ejemplo. */
  subtitulo?: string;
  /** Denominador de la escala: 75 = 1:75. */
  escala?: number;
  /** Margen de papel alrededor del dibujo, en milímetros. */
  margen?: number;
}

/** Los números del PDF no admiten notación exponencial: `1e-7` rompe el parser. */
const num = (v: number) => (Math.round(v * 1e3) / 1e3).toFixed(3).replace(/\.?0+$/, '') || '0';

/** Un número para LEER: dos decimales y coma, como se escribe un plano acá. */
const cifra = (v: number) => v.toFixed(2).replace('.', ',');

/**
 * Texto listo para meter entre paréntesis en el flujo del PDF.
 *
 * Los acentos NO se pueden escribir tal cual: el archivo se concatena como
 * cadena de JavaScript y se mide en bytes UTF-8, pero la fuente lo lee en
 * WinAnsi, donde «Á» es UN byte (0xC1). Escaparlo en octal deja el archivo
 * entero en ASCII puro, así que lo que se escribe y lo que se cuenta coinciden
 * —y «ÁREA» no sale «Ã�REA»—.
 *
 * WinAnsi coincide con Latin-1 en todo lo que usamos (vocales acentuadas, ñ, ·
 * y el ² de los metros cuadrados), así que el punto de código ES el byte.
 */
function texto(s: string): string {
  let out = '';
  for (const ch of s) {
    const c = ch.codePointAt(0)!;
    if (ch === '(' || ch === ')' || ch === '\\') out += `\\${ch}`;
    else if (c >= 32 && c < 127) out += ch;
    else if (c <= 255) out += `\\${c.toString(8).padStart(3, '0')}`;
    // Fuera de Latin-1 no hay byte que escribir: antes un signo de pregunta que
    // un archivo corrupto.
    else out += '?';
  }
  return out;
}

/** Los vértices de un área creada. `null` cuando su forma son celdas sueltas. */
function contornoDe(l: Libre): Array<[number, number]> | null {
  if (l.vertices?.length) return l.vertices;
  if (l.celdas?.length) return anilloDeCeldas(l.celdas);
  return [[l.x, l.y], [l.x + l.w, l.y], [l.x + l.w, l.y + l.h], [l.x, l.y + l.h]];
}

/**
 * Hasta dónde llega todo lo que se va a dibujar, en metros.
 *
 * Mira las paredes Y las áreas creadas, no sólo las paredes: la cochera no
 * tiene muros y encuadrar con las paredes la dejaba fuera del papel. Es el
 * mismo motivo por el que existe `Planta.marco`, y si la planta lo trae, manda
 * ése —el usuario ya decidió hasta dónde llega el dibujo—.
 */
function extensión(planta: Planta): [number, number, number, number] {
  if (planta.marco) return planta.marco;
  let x0 = Infinity; let x1 = -Infinity; let y0 = Infinity; let y1 = -Infinity;
  const meter = (ax: number, ay: number, bx: number, by: number) => {
    x0 = Math.min(x0, ax, bx); x1 = Math.max(x1, ax, bx);
    y0 = Math.min(y0, ay, by); y1 = Math.max(y1, ay, by);
  };
  for (const w of planta.paredes) {
    const [ax, ay, bx, by] = caja(w);
    meter(ax, ay, bx, by);
  }
  for (const l of planta.libres ?? []) meter(l.x, l.y, l.x + l.w, l.y + l.h);
  if (!Number.isFinite(x0)) return [0, 10, 0, 10];
  return [x0, x1, y0, y1];
}

// ─── El mobiliario ──────────────────────────────────────────────────────────

/**
 * Un árbol de React visto como nodos de dibujo, sin pasar por el DOM.
 *
 * `SimboloMueble` devuelve el MISMO dibujo que se ve en pantalla, así que esto
 * es lo que garantiza que el PDF y el editor no se separen: si alguien retoca
 * el símbolo de un inodoro, se retoca en los dos a la vez. El camino obvio
 * —`renderToStaticMarkup` + `DOMParser`, que es el que usa `exportar-simbolo`—
 * se descartó porque ata el escritor al navegador y el banco de pruebas corre
 * en Node: una lámina que sólo se puede probar a ojo no se puede probar.
 *
 * Los componentes se INVOCAN (`el.type(el.props)`) en vez de renderizarse. Vale
 * porque los símbolos del catálogo son funciones puras de `(w, h, m)` sin
 * estado ni efectos; el día que alguno use un hook, esto lanza y el mueble se
 * salta (ver el `try` de `trazoDeMueble`), que es mejor que tumbar la lámina.
 */
function comoNodos(n: ReactNode): NodoSVG[] {
  if (n == null || typeof n === 'boolean' || typeof n === 'string' || typeof n === 'number') return [];
  if (Array.isArray(n)) return n.flatMap(comoNodos);

  const el = n as ReactElement<Record<string, unknown>>;
  if (!el || !el.type) return [];
  const hijos = () => comoNodos((el.props as { children?: ReactNode })?.children);

  if (typeof el.type === 'function') {
    return comoNodos((el.type as (p: unknown) => ReactNode)(el.props));
  }
  // Un `Fragment` es un símbolo, no una etiqueta: lo que dibuja son sus hijos.
  if (typeof el.type !== 'string') return hijos();

  return [{
    tag: el.type,
    attr: (a) => {
      const v = (el.props as Record<string, unknown>)[a];
      return v == null ? null : String(v);
    },
    hijos: hijos(),
  }];
}

/**
 * El dibujo de un mueble, en METROS y ya puesto donde va en el plano.
 *
 * Sale ubicado porque el `<g transform>` de `SimboloMueble` ya lleva el
 * traslado al sitio, el giro y el espejo, y `desdeArbol` acumula las matrices
 * al bajar. Así no hay una segunda versión de «dónde va cada mueble» que se
 * pueda desfasar de la de pantalla.
 *
 * `null` cuando el símbolo no deja nada que pintar: una pieza sin trazo, un
 * componente que no se pudo invocar. Un mueble menos es mejor que un PDF menos.
 */
function trazoDeMueble(m: Mueble): string[] | null {
  try {
    const raiz = comoNodos(createElement(SimboloMueble, { m }))[0];
    if (!raiz) return null;
    return desdeArbol(raiz).d;
  } catch {
    return null;
  }
}

/** Los caminos `M/L/C/Z` del importador como operadores de pintado del PDF. */
function aOperadores(d: string, X: (v: number) => number, Y: (v: number) => number): string[] {
  const ops: string[] = [];
  for (const [, c, crudo] of d.matchAll(/([MLCZ])([^MLCZ]*)/g)) {
    if (c === 'Z') { ops.push('h'); continue; }
    const n = (crudo.match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi) ?? []).map(Number);
    const letra = c === 'M' ? 'm' : c === 'L' ? 'l' : 'c';
    const paso = c === 'C' ? 6 : 2;
    for (let i = 0; i + paso <= n.length; i += paso) {
      const p: string[] = [];
      for (let j = 0; j + 1 < paso; j += 2) p.push(num(X(n[i + j])), num(Y(n[i + j + 1])));
      ops.push(`${p.join(' ')} ${letra}`);
    }
  }
  return ops;
}

// ─── Las cotas ──────────────────────────────────────────────────────────────

/** Cuerpo de letra del número de cota. Medido en la lámina: 5,2 pt ArialMT. */
const COTA = 5.2;
/** Largo de la marca inclinada de la punta. Medido: 2,64 pt. */
const MARCA = 2.64;
/** Cuánto sobresale la línea de referencia pasada la línea de cota. Medido: 4,4 pt. */
const SOBRA = 4.4;
/** Aire entre el borde del plano y el arranque de la línea de referencia. */
const AIRE = 1.5;
/** Separación de la cadena de parciales al borde del plano. Medido: 6,8–10,1 pt. */
const SEP_PARCIAL = 9;
/**
 * La cadena del total, por fuera de la de parciales.
 *
 * El hueco contra `SEP_PARCIAL` no es decorativo: ahí se apilan los números de
 * los tramos angostos que no entran sobre su propia línea (ver `cadena`). Con
 * los 22 de antes, el tercer renglón se montaba sobre la cadena del total.
 */
const SEP_TOTAL = 28;
/** La marca va a 75° de la línea de cota: es la pestaña clásica del CAD. */
const COS75 = Math.cos((75 * Math.PI) / 180);
const SEN75 = Math.sin((75 * Math.PI) / 180);
/** El rayado del muro va a 60°, que es el ángulo medido en la lámina. */
const SEN60 = Math.sin((60 * Math.PI) / 180);
const TAN60 = Math.tan((60 * Math.PI) / 180);

/**
 * Dos ejes a menos de esto son la MISMA pared leída dos veces, no dos medidas.
 *
 * El importador saca los ejes de masas de relleno, y un muro dibujado en dos
 * tramos casi alineados deja ejes a 2 o 3 cm. Rotular `0,02` en una cadena no
 * es precisión: es ruido de extracción ocupando el lugar de una cota. El tope
 * es corto a propósito —el muro más fino del catálogo mide 7 cm— así que esto
 * nunca puede fundir dos paredes de verdad.
 */
const FUSION = 0.05;

/**
 * Los ejes de pared distintos en un sentido, ordenados. Son los cortes de la
 * cadena, ya fundidos: cada grupo de ejes casi coincidentes se representa por
 * su promedio. Como la cadena de parciales y la del total se arman las dos de
 * ESTA lista, cierran por construcción.
 */
function ejesDe(paredes: Pared[], o: 'h' | 'v'): number[] {
  const crudos = [...new Set(
    paredes.filter((w) => w.o === o).map((w) => Math.round(w.c * 1000) / 1000),
  )].sort((a, b) => a - b);

  const ejes: number[] = [];
  let grupo: number[] = [];
  const cerrar = () => {
    if (grupo.length) ejes.push(grupo.reduce((s, v) => s + v, 0) / grupo.length);
    grupo = [];
  };
  for (const v of crudos) {
    if (grupo.length && v - grupo[grupo.length - 1] >= FUSION) cerrar();
    grupo.push(v);
  }
  cerrar();
  return ejes;
}

/**
 * Una cadena de cotas FUERA del plano, como en una lámina de obra.
 *
 * Es lo que Daniel reclamó: «las cotas deben verse como en el plano, que están
 * alejadas y tienen líneas de referencia». Cada corte trae su línea de
 * referencia desde el borde del dibujo, la línea de cota los enhebra a todos, y
 * cada punta lleva su marca inclinada. El número va sobre la línea, nunca
 * encima del muro y nunca de cabeza.
 *
 * Trabaja en PUNTOS de papel: los metros ya quedaron atrás. `P(a, b)` arma el
 * punto según el sentido —`a` corre a lo largo de la cadena y `b` es la
 * coordenada fija—, y con eso la cadena de abajo y la de la izquierda son el
 * mismo código en vez de dos copias que se van separando.
 */
function cadena(
  ops: string[],
  cortes: Array<{ en: number; metros: number }>,
  nivel: number,
  borde: number,
  horizontal: boolean,
): void {
  if (cortes.length < 2) return;
  const P = (a: number, b: number): [number, number] => (horizontal ? [a, b] : [b, a]);
  // Hacia dónde se aleja la cadena del dibujo. Siempre hacia afuera: la cadena
  // de abajo baja y la de la izquierda va a la izquierda, las dos en negativo.
  const fuera = -1;

  ops.push('0 G 0.25 w');
  const a0 = cortes[0].en;
  const a1 = cortes[cortes.length - 1].en;
  ops.push(`${num(P(a0, nivel)[0])} ${num(P(a0, nivel)[1])} m`
    + ` ${num(P(a1, nivel)[0])} ${num(P(a1, nivel)[1])} l S`);

  for (const { en } of cortes) {
    // La línea de referencia: del borde del plano hasta pasada la línea de cota.
    const d0 = P(en, borde + fuera * AIRE);
    const d1 = P(en, nivel + fuera * SOBRA);
    ops.push(`0.6 G 0.2 w ${num(d0[0])} ${num(d0[1])} m ${num(d1[0])} ${num(d1[1])} l S`);
    // La marca inclinada: la dirección de la cadena girada 75°.
    const [ux, uy] = horizontal ? [1, 0] : [0, 1];
    const [tx, ty] = [ux * COS75 - uy * SEN75, ux * SEN75 + uy * COS75];
    const [cx, cy] = P(en, nivel);
    ops.push(`0 G 0.35 w ${num(cx - tx * MARCA / 2)} ${num(cy - ty * MARCA / 2)} m`
      + ` ${num(cx + tx * MARCA / 2)} ${num(cy + ty * MARCA / 2)} l S`);
  }

  // Los números, uno por tramo, centrados y levantados del lado del plano.
  //
  // NINGÚN tramo queda mudo. Antes, el que no daba el ancho del número se
  // saltaba, y eso rompe lo único que una cadena promete: que sus partes sumen
  // el todo. En el Salamanca se perdían 0,16 · 0,17 · 0,31 · 0,25 m — la cadena
  // seguía dibujada entera, pero los números leídos no daban el total y el
  // plano quedaba desmintiéndose a sí mismo.
  //
  // Lo que hace un CAD con una cota angosta es correr el número a un renglón de
  // más afuera, no borrarlo. `renglones` lleva hasta dónde llegó el último
  // número de cada renglón, y el tramo toma el primero donde entra: el 0 es
  // sobre la línea de cota (solo si cabe dentro del tramo) y los siguientes van
  // saliendo. La marca inclinada ya dice a qué tramo pertenece cada uno.
  const renglones: number[] = [];
  for (let i = 0; i + 1 < cortes.length; i++) {
    const s = cifra(cortes[i + 1].metros);
    const ancho = s.length * COTA * 0.5;
    const medio = (cortes[i].en + cortes[i + 1].en) / 2;
    const desde = medio - ancho / 2;

    const cabeDentro = Math.abs(cortes[i + 1].en - cortes[i].en) >= ancho;
    let r = cabeDentro ? 0 : 1;
    // Un renglón de afuera se comparte entre varios tramos angostos: hay que
    // buscar uno donde el número no pise al anterior.
    while (r > 0 && renglones[r] != null && desde < renglones[r]) r++;
    renglones[r] = desde + ancho + 1.5;

    // Renglón 0 = pegado a la línea, del lado del plano. Los de afuera se
    // apilan en el sentido en que la cadena se aleja del dibujo.
    const alto = r === 0 ? nivel - fuera * 1.6 : nivel + fuera * (r * (COTA + 1.2) - 1.6);
    if (horizontal) {
      ops.push(`BT /F1 ${COTA} Tf 1 0 0 1 ${num(desde)} ${num(alto)} Tm`
        + ` (${texto(s)}) Tj ET`);
    } else {
      // Girado 90°: en una cadena vertical el número se lee de abajo hacia
      // arriba, que es la convención de cualquier plano.
      ops.push(`BT /F1 ${COTA} Tf 0 1 -1 0 ${num(alto)} ${num(desde)} Tm`
        + ` (${texto(s)}) Tj ET`);
    }
  }
}

// ─── La lámina ──────────────────────────────────────────────────────────────

/** El relleno de fondo de cada clase de área, en gris/color muy lavado. */
const FONDO: Record<Clase, string> = {
  interna: '0.937 0.949 0.969 rg',
  externa: '0.980 0.953 0.906 rg',
  lote: '0.949 0.969 0.933 rg',
  azotea: '0.957 0.957 0.957 rg',
};

/**
 * La planta como PDF de una página.
 *
 * Se escriben las coordenadas YA convertidas a puntos en vez de dejar una
 * matriz `cm` global haciendo la conversión. Con matriz, el grosor de línea y
 * el cuerpo de letra quedan atados a la escala del dibujo: a 1:75 una línea de
 * 0,15 pt saldría de 0,002 pt y las letras serían invisibles. Convirtiendo
 * acá, lo que se dibuja va en metros y lo que se LEE va en puntos de papel,
 * que es como se piensa una lámina.
 */
export function aPDFLamina(planta: Planta, med: Medicion, op: OpcionesLamina): string {
  const escala = op.escala ?? ESCALA_POR_DEFECTO;
  const margen = (op.margen ?? 15) * MM;
  const k = PT_POR_METRO / escala;          // puntos de papel por metro de casa

  const [mx0, mx1, my0, my1] = extensión(planta);
  const ancho = (mx1 - mx0) * k;
  const alto = (my1 - my0) * k;

  // El bloque de áreas y el título no se dibujan ENCIMA del plano: se les
  // reserva su propia banda, arriba el título y abajo las áreas. Un número
  // pisando una pared es un número que nadie usa. Y las cadenas de cota quieren
  // su propio aire por fuera del dibujo, o quedan pegadas al margen de corte.
  const BANDA_TITULO = 18 * MM;
  // +1 por la línea del total, y una más cuando la planta no cerró y hay que
  // explicar por qué en vez de rotular un cero.
  const filas = CLASES.filter((c) => med[c] > 0).length + 1 + (med.total > 0 ? 0 : 1);
  const BANDA_AREAS = (10 + filas * 5) * MM;
  const AIRE_COTAS = SEP_TOTAL + SOBRA + 4;

  const W = ancho + margen * 2 + AIRE_COTAS;
  const H = alto + margen * 2 + AIRE_COTAS + BANDA_TITULO + BANDA_AREAS;

  /** Metros del modelo → puntos de página. La y se voltea: el PDF crece hacia
   *  arriba y el editor hacia abajo. */
  const X = (x: number) => margen + AIRE_COTAS + (x - mx0) * k;
  const Y = (y: number) => margen + BANDA_AREAS + AIRE_COTAS + alto - (y - my0) * k;

  // El borde del dibujo en el papel: de ahí arrancan las líneas de referencia.
  const izq = X(mx0);
  const abajo = Y(my1);

  const ops: string[] = [];
  const escribir = (x: number, y: number, cuerpo: number, s: string, fuente = 'F1') => {
    ops.push(`BT /${fuente} ${num(cuerpo)} Tf 1 0 0 1 ${num(x)} ${num(y)} Tm (${texto(s)}) Tj ET`);
  };
  /** Texto centrado. El ancho se estima a 0,5 em por letra —Helvetica ronda eso
   *  en minúsculas— porque acá no hay tabla de métricas y basta para centrar. */
  const centrado = (x: number, y: number, cuerpo: number, s: string, fuente = 'F1') => {
    escribir(x - (s.length * cuerpo * 0.5) / 2, y, cuerpo, s, fuente);
  };
  /** Un polígono en metros, como camino cerrado en puntos. */
  const camino = (vs: Array<[number, number]>) => {
    ops.push(`${num(X(vs[0][0]))} ${num(Y(vs[0][1]))} m`);
    for (const [vx, vy] of vs.slice(1)) ops.push(`${num(X(vx))} ${num(Y(vy))} l`);
    ops.push('h');
  };

  // ── 1 · Fondos: el piso de cada región, teñido según de qué lado suma ──
  // Es lo primero que se pinta porque va DEBAJO de todo: muros, muebles y cotas
  // se leen encima. Sin esto el plano son líneas al aire, que es parte de lo
  // que Daniel llamó «faltan los fondos».
  for (const r of med.regiones) {
    const anillo = anilloDeCeldas(r.celdas);
    ops.push(FONDO[r.clase] ?? FONDO.interna);
    if (anillo && anillo.length >= 3) { camino(anillo); ops.push('f'); continue; }
    // Sin anillo —una región partida en islas— se pinta celda por celda.
    for (const [cx, cy, cw, ch] of r.celdas) {
      ops.push(`${num(X(cx))} ${num(Y(cy + ch))} ${num(cw * k)} ${num(ch * k)} re f`);
    }
  }

  // ── 2 · Áreas dibujadas a mano: fondo propio y contorno fino ──
  for (const l of planta.libres ?? []) {
    const vs = contornoDe(l);
    if (!vs || vs.length < 3) continue;
    ops.push(FONDO[l.clase ?? 'externa'] ?? FONDO.externa);
    camino(vs); ops.push('f');
    ops.push('0.45 G 0.4 w');
    camino(vs); ops.push('S');
  }

  // ── 3 · Muros: blancos por dentro, rayados y perfilados ──
  // Macizos NO: un muro negro a 1:75 se come la trama y el plano deja de decir
  // de qué está hecho. La lámina de referencia los lleva perfilados con un
  // rayado adentro, y eso es lo que se copia acá —con el ángulo medido, 60°—.
  // Se pintan por TRAMOS y no de a a b: un vano dibujado relleno es una puerta
  // tapiada, y el plano dejaría de decir por dónde se entra.
  const PASO = 1.1;                                  // separación del rayado, en puntos
  const PASO_X = PASO / SEN60;
  /** Un pedazo de muro: blanco por dentro, rayado a 60° y perfilado. */
  const muro = (ax: number, ay: number, bx: number, by: number): void => {
    const [px, py] = [X(ax), Y(by)];
    const [pw, ph] = [(bx - ax) * k, (by - ay) * k];
    if (pw <= 0 || ph <= 0) return;
    ops.push(`1 g ${num(px)} ${num(py)} ${num(pw)} ${num(ph)} re f`);
    // El rayado se recorta al muro (`W n`) en vez de calcular dónde entra y
    // dónde sale cada línea: el recorte lo hace el visor, exacto y gratis.
    ops.push(`q ${num(px)} ${num(py)} ${num(pw)} ${num(ph)} re W n`);
    ops.push('0.55 G 0.18 w');
    // `corrido` es cuánto se desplaza la línea en x al subir todo el muro;
    // el paso en x es el perpendicular dividido por el seno, o el rayado
    // saldría más apretado cuanto más inclinado.
    const corrido = ph / TAN60;
    // La fase se mide desde el ORIGEN de la hoja, no desde el borde izquierdo
    // de este pedazo. Así el rayado de dos pedazos vecinos es la misma familia
    // de líneas y no se nota dónde termina uno —antes cada tramo arrancaba su
    // propia fase y en cada corte quedaba un saltito—.
    const inicio = py / TAN60
      + Math.ceil(((px - corrido) - py / TAN60) / PASO_X) * PASO_X;
    for (let c = inicio; c < px + pw; c += PASO_X) {
      ops.push(`${num(c)} ${num(py)} m ${num(c + corrido)} ${num(py + ph)} l S`);
    }
    ops.push('Q');
    ops.push(`0 G 0.5 w ${num(px)} ${num(py)} ${num(pw)} ${num(ph)} re S`);
  };
  for (const w of planta.paredes) {
    for (const [a, b] of tramos(w)) muro(...caja(w, a, b));
  }
  // El cuadrado del codo, DESPUÉS de los muros y a propósito. Con las puntas en
  // el eje —que es lo que deja la cota en su medida— cada pared cubre sólo su
  // mitad del cuadrado donde se cruzan y en la esquina de afuera queda un
  // cuadrante en blanco: el «escalón». Pintarlo al final hace dos cosas de una:
  // rellena esa muesca y tapa las dos líneas de perfil que cada muro dejaba
  // cruzando el codo por dentro. Los cuatro lados del cuadrado caen sobre caras
  // de pared reales, así que el perfil no inventa ninguna arista.
  for (const r of cuadradosDeEsquina(planta.paredes)) muro(...r);

  // ── 4 · Mobiliario ──
  // El MISMO dibujo que se ve en pantalla, traído sin pasar por el navegador.
  ops.push('0.35 G 0.3 w 1 J 1 j');
  for (const m of planta.muebles ?? []) {
    const d = trazoDeMueble(m);
    if (!d) continue;
    for (const camino1 of d) {
      const trazos = aOperadores(camino1, X, Y);
      if (trazos.length) ops.push(...trazos, 'S');
    }
  }
  ops.push('0 J 0 j');

  // ── 5 · Rótulos: el nombre del recinto Y su área ──
  // La lámina de referencia no los trae del CAD (los suyos son una anotación
  // pegada encima, en otra tipografía) y tampoco rotula el m². Acá sí: es lo
  // primero que se busca en un plano de venta, y el dato ya está medido.
  ops.push('0 g');
  for (const r of med.regiones) {
    // `nombre` arrastra el «Sin rótulo» y el «1/2/3» que el EDITOR necesita
    // para indexar el reposo por una llave única. `rotulo` es la misma región
    // dicha como la escribiría un plano, y viene vacío cuando nadie la nombró:
    // ahí la región habla sola con su área, que es el dato que sí está medido.
    // Rotular «Sin rótulo 2 · 0,97 m²» en una lámina que se entrega es ruido
    // que además no cabe en un cuarto de 0,97 m².
    if (r.rotulo) {
      centrado(X(r.x), Y(r.y), 6, r.rotulo, 'F2');
      centrado(X(r.x), Y(r.y) - 5, 5, `${cifra(r.area)} m²`);
    } else {
      // Sin nombre el bloque es de UNA línea: se corre media línea para quedar
      // centrado en la misma ancla, en vez de colgar del renglón de arriba.
      centrado(X(r.x), Y(r.y) - 2.5, 5, `${cifra(r.area)} m²`);
    }
  }
  // Un recinto que cae DENTRO de una región medida ya quedó dicho en el rótulo
  // de esa región. Se descarta por PERTENENCIA y no por nombre, que es lo que
  // hacía esto antes y era un choque garantizado: la región «Pasillo + S.S +
  // Dormitorio» no se llama «Pasillo», así que el recinto Pasillo se volvía a
  // escribir —y su ancla es la MISMA que la de la región, porque la región
  // hereda la del primer recinto que tiene adentro—. Las dos tiras de texto
  // salían una encima de la otra, exactas, y se leían como tres nombres
  // apilados. Quedan sólo los recintos que ninguna región encerró: el aviso de
  // que ese cuarto está abierto al exterior.
  const medidas = new Set(med.regiones.map((r) => r.indice));
  for (const r of planta.recintos) {
    if (r.region != null && medidas.has(r.region)) continue;
    centrado(X(r.x), Y(r.y), 6, r.nombre, 'F2');
  }
  for (const l of med.libres) {
    const area = planta.libres?.find((q) => q.id === l.id);
    if (!area) continue;
    // El lote NO se rotula en el medio. Su caja encierra la casa entera, así
    // que su centro cae SIEMPRE encima del plano: el rótulo salía pisando
    // cuartos y cotas. Va a su esquina de arriba a la izquierda —donde una
    // lámina anota el terreno— y alineado a la izquierda, en una sola línea,
    // para no meterse en el dibujo.
    if (l.clase === 'lote') {
      escribir(X(area.x) + 3, Y(area.y) - 9, 6, `${l.nombre}  ·  ${cifra(l.area)} m²`, 'F2');
      continue;
    }
    centrado(X(area.x + area.w / 2), Y(area.y + area.h / 2), 6, l.nombre, 'F2');
    centrado(X(area.x + area.w / 2), Y(area.y + area.h / 2) - 5, 5, `${cifra(l.area)} m²`);
  }

  // ── 6 · Las cotas, afuera y en cadena ──
  const ejesV = ejesDe(planta.paredes, 'v');   // posiciones en x → cadena de abajo
  const ejesH = ejesDe(planta.paredes, 'h');   // posiciones en y → cadena de la izquierda
  if (ejesV.length >= 2) {
    cadena(ops, ejesV.map((v, i) => ({ en: X(v), metros: v - (ejesV[i - 1] ?? v) })),
      abajo - SEP_PARCIAL, abajo, true);
    cadena(ops, [
      { en: X(ejesV[0]), metros: 0 },
      { en: X(ejesV[ejesV.length - 1]), metros: ejesV[ejesV.length - 1] - ejesV[0] },
    ], abajo - SEP_TOTAL, abajo, true);
  }
  if (ejesH.length >= 2) {
    // De mayor a menor: la `y` del papel crece hacia ARRIBA y la del modelo
    // hacia abajo, así que la cadena se recorre al revés. Invertir después de
    // calcular los parciales rotularía cada tramo con la medida del vecino.
    const bajando = [...ejesH].reverse();
    cadena(ops, bajando.map((v, i) => ({ en: Y(v), metros: (bajando[i - 1] ?? v) - v })),
      izq - SEP_PARCIAL, izq, false);
    cadena(ops, [
      { en: Y(bajando[0]), metros: 0 },
      { en: Y(bajando[bajando.length - 1]), metros: bajando[0] - bajando[bajando.length - 1] },
    ], izq - SEP_TOTAL, izq, false);
  }

  // Las cotas puestas a mano se respetan donde el usuario las dejó: son suyas.
  ops.push('0 G 0.25 w');
  for (const c of (planta.cotas ?? []) as Cota[]) {
    const largo = Math.hypot(c.x2 - c.x1, c.y2 - c.y1);
    if (largo < 0.2) continue;
    // La separación se aplica EN METROS, antes de pasar por `X`/`Y`. Hacerlo
    // después obligaría a invertirle el signo a mano —el PDF cuenta la `y`
    // hacia arriba y la planta hacia abajo— y una cota arrastrada hacia la
    // cocina saldría impresa hacia el patio. Ver `Cota.off`.
    const off = c.off ?? 0;
    const nmx = -(c.y2 - c.y1) / largo, nmy = (c.x2 - c.x1) / largo;
    const [ax, ay] = [X(c.x1 + nmx * off), Y(c.y1 + nmy * off)];
    const [bx, by] = [X(c.x2 + nmx * off), Y(c.y2 + nmy * off)];
    // Los testigos, cuando la cota está separada: van del punto MEDIDO a la
    // línea de cota y la pasan de largo `SOBRA`, igual que los de las cadenas
    // automáticas de más arriba. Sin ellos, una cota despegada impresa es un
    // número suelto al lado del plano.
    if (off !== 0) {
      for (const [qx, qy, rx, ry] of [
        [X(c.x1), Y(c.y1), ax, ay], [X(c.x2), Y(c.y2), bx, by],
      ]) {
        const l = Math.max(Math.hypot(rx - qx, ry - qy), 1e-6);
        const [ex, ey] = [(rx - qx) / l, (ry - qy) / l];
        ops.push(`${num(qx + ex * AIRE)} ${num(qy + ey * AIRE)} m`
          + ` ${num(rx + ex * SOBRA)} ${num(ry + ey * SOBRA)} l S`);
      }
    }
    const d = Math.hypot(bx - ax, by - ay);
    const [ux, uy] = [(bx - ax) / d, (by - ay) / d];
    const [px, py] = [-uy, ux];
    ops.push(`${num(ax)} ${num(ay)} m ${num(bx)} ${num(by)} l S`);
    const [tx, ty] = [ux * COS75 - uy * SEN75, ux * SEN75 + uy * COS75];
    for (const [qx, qy] of [[ax, ay], [bx, by]]) {
      ops.push(`${num(qx - tx * MARCA / 2)} ${num(qy - ty * MARCA / 2)} m`
        + ` ${num(qx + tx * MARCA / 2)} ${num(qy + ty * MARCA / 2)} l S`);
    }
    const rot = ux < 0 ? [-ux, -uy] : [ux, uy];   // nada boca abajo
    const s = cifra(largo);
    const mx = (ax + bx) / 2 - (rot[0] * s.length * COTA * 0.5) / 2 + px * COTA * 0.35;
    const my = (ay + by) / 2 - (rot[1] * s.length * COTA * 0.5) / 2 + py * COTA * 0.35;
    ops.push(`BT /F1 ${COTA} Tf ${num(rot[0])} ${num(rot[1])} ${num(-rot[1])} ${num(rot[0])}`
      + ` ${num(mx)} ${num(my)} Tm (${texto(s)}) Tj ET`);
  }

  // ── 7 · Título ──
  ops.push('0 g');
  const yTit = H - margen - 9 * MM;
  escribir(margen, yTit, 12, op.titulo, 'F2');
  escribir(margen, yTit - 6 * MM, 8, `${op.subtitulo ? `${op.subtitulo}  ·  ` : ''}Escala 1:${escala}`);

  // ── 8 · Bloque de áreas ──
  // Las cuatro clases son columnas INDEPENDIENTES (ver `Clase` en tipos.ts), así
  // que se listan las que tienen algo y el total NO es su suma: el total es el
  // área interna, que es la que se compara contra el modelo y fija el precio.
  let yb = margen + BANDA_AREAS - 6 * MM;
  escribir(margen, yb, 8, 'CUADRO DE ÁREAS', 'F2');
  for (const c of CLASES) {
    if (med[c] <= 0) continue;
    yb -= 5 * MM;
    escribir(margen, yb, 7, `ÁREA ${ROTULO_CLASE[c].toUpperCase()}:`);
    escribir(margen + 38 * MM, yb, 7, `${cifra(med[c])} m²`, 'F2');
  }
  yb -= 5 * MM;
  escribir(margen, yb, 7, 'ÁREA TOTAL DE LA CASA:');
  // Un cero NO es una medición: es que no se midió. Pasa de verdad —la planta
  // baja del Salamanca se abre a la cochera, así que no cierra ninguna región—
  // y hasta acá la lámina rotulaba «0,00 m²» con la misma cara con la que
  // rotula un área buena. En un entregable que ve un cliente eso es peor que
  // no decir nada: el cero se lee como dato. Si la planta no cerró, lo dice.
  if (med.total > 0) {
    escribir(margen + 38 * MM, yb, 7, `${cifra(med.total)} m²`, 'F2');
  } else {
    escribir(margen + 38 * MM, yb, 7, 'sin medir: la planta no cierra', 'F2');
    yb -= 4 * MM;
    const por = med.sueltos.length
      ? `Quedaron fuera de toda región cerrada: ${med.sueltos.join(', ')}.`
      : 'Ningún recinto quedó encerrado por paredes.';
    escribir(margen, yb, 6, por);
  }

  const flujo = `${ops.join('\n')}\n`;

  // Un PDF mínimo: catálogo, páginas, la página, su contenido y las dos fuentes.
  // Helvetica y Helvetica-Bold son de las 14 estándar, así que no se incrustan:
  // todo visor las tiene. Igualar la ArialMT de las láminas del catálogo pide
  // incrustar Liberation Sans, que es su clon métrico, y eso va con el cajetín.
  const objetos = [
    '<</Type/Catalog/Pages 2 0 R>>',
    '<</Type/Pages/Kids[3 0 R]/Count 1>>',
    `<</Type/Page/Parent 2 0 R/MediaBox[0 0 ${num(W)} ${num(H)}]/Contents 4 0 R`
      + '/Resources<</Font<</F1 5 0 R/F2 6 0 R>>>>>>',
    `<</Length ${new TextEncoder().encode(flujo).length}>>\nstream\n${flujo}endstream`,
    '<</Type/Font/Subtype/Type1/BaseFont/Helvetica/Encoding/WinAnsiEncoding>>',
    '<</Type/Font/Subtype/Type1/BaseFont/Helvetica-Bold/Encoding/WinAnsiEncoding>>',
  ];

  let pdf = '%PDF-1.4\n';
  const donde: number[] = [];
  objetos.forEach((o, i) => {
    donde.push(new TextEncoder().encode(pdf).length);
    pdf += `${i + 1} 0 obj\n${o}\nendobj\n`;
  });
  const xref = new TextEncoder().encode(pdf).length;
  pdf += `xref\n0 ${objetos.length + 1}\n0000000000 65535 f \n`
    + donde.map((d) => `${String(d).padStart(10, '0')} 00000 n \n`).join('')
    + `trailer\n<</Size ${objetos.length + 1}/Root 1 0 R>>\n`
    + `startxref\n${xref}\n%%EOF\n`;
  return pdf;
}

/** Nombre de archivo sin acentos, sin barras y sin espacios. */
function aNombreDeArchivo(nombre: string): string {
  return (nombre.normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase()
    || 'planta');
}

/**
 * Baja la lámina al disco.
 *
 * El `revokeObjectURL` no es higiene opcional: sin él los bytes del archivo
 * quedan vivos en memoria hasta que se recargue la página.
 */
export function bajarLamina(planta: Planta, med: Medicion, op: OpcionesLamina): void {
  const url = URL.createObjectURL(
    new Blob([aPDFLamina(planta, med, op)], { type: 'application/pdf' }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = `${aNombreDeArchivo(op.subtitulo || op.titulo)}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}
